/**
 * Filter navigation items/sections based on user's effective capabilities.
 *
 * Supports both single-capability and multi-capability (ANY) requirements.
 * Removes sections that have no visible items.
 */

import { getRequiredCapability } from './navigationCapabilityMap';

/**
 * Check if a user has a required capability or set of capabilities.
 *
 * @param {string | string[] | null} requiredCapability - Single capability, array (ANY), or null
 * @param {string[]} effectiveCapabilities - User's effective capabilities
 * @returns {boolean} - true if user has required capability/capabilities
 */
function userHasCapability(requiredCapability, effectiveCapabilities) {
  if (requiredCapability === null) return true; // No requirement = always visible
  if (!effectiveCapabilities || effectiveCapabilities.length === 0) return false;

  if (Array.isArray(requiredCapability)) {
    // Array = ANY of these capabilities
    return requiredCapability.some((cap) => effectiveCapabilities.includes(cap));
  }

  // Single string capability
  return effectiveCapabilities.includes(requiredCapability);
}

/**
 * Filter a navigation item based on required capabilities.
 *
 * @param {object} item - Navigation item { id, label, path, ... }
 * @param {string[]} effectiveCapabilities - User's effective capabilities
 * @returns {boolean} - true if item should be visible
 */
export function isNavigationItemVisible(item, effectiveCapabilities) {
  if (!item) return false;

  const requiredCapability = getRequiredCapability(item.id);
  return userHasCapability(requiredCapability, effectiveCapabilities);
}

/**
 * Filter an array of navigation items by capability.
 *
 * @param {object[]} items - Navigation items to filter
 * @param {string[]} effectiveCapabilities - User's effective capabilities
 * @returns {object[]} - Filtered items (only visible ones)
 */
export function filterNavigationItems(items, effectiveCapabilities) {
  if (!Array.isArray(items)) return [];
  return items.filter((item) => isNavigationItemVisible(item, effectiveCapabilities));
}

/**
 * Filter an array of navigation sections, removing empty sections.
 *
 * @param {object[]} sections - Navigation sections { id, title, items: [...] }
 * @param {string[]} effectiveCapabilities - User's effective capabilities
 * @returns {object[]} - Filtered sections with filtered items; empty sections removed
 */
export function filterNavigationSections(sections, effectiveCapabilities) {
  if (!Array.isArray(sections)) return [];

  return sections
    .map((section) => {
      if (!section || !Array.isArray(section.items)) return null;

      const visibleItems = filterNavigationItems(section.items, effectiveCapabilities);

      // Remove section if it has no visible items
      if (visibleItems.length === 0) return null;

      return {
        ...section,
        items: visibleItems,
      };
    })
    .filter(Boolean); // Remove null sections
}

/**
 * Create a capability-aware navigation item wrapper.
 *
 * Useful when building items inline. Marks items with their required capability.
 *
 * @param {object} item - Navigation item
 * @param {string | string[] | null} requiredCapability - Required capability(ies) or null
 * @returns {object} - Item with capability metadata
 */
export function createCapabilityAwareItem(item, requiredCapability) {
  return {
    ...item,
    __requiredCapability: requiredCapability,
  };
}
